"use client";

import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  User,
  Mail,
  Phone,
  Lock,
  ShieldCheck,
  CreditCard,
  Loader2,
} from "lucide-react";
import {useRazorpay} from "react-razorpay";
import { toast } from "sonner";
import { Apirequests } from "../../utils/ApiRequests";
import { handlePaymentNew, handleRazorPayPayment } from "../../utils/paymenthelper";

export default function TestPayment() {
  const {Razorpay} = useRazorpay();

  const thankYouPath = "#thank-you-test";

  const [loading, setLoading] = useState(false);
  const [fetchingCourse, setFetchingCourse] = useState(true);
  const [showThankYou, setShowThankYou] = useState(false);

  const [courseId, setCourseId] = useState("");
  const [courseData, setCourseData] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [finalPrice, setFinalPrice] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    whatsapp_number: "",
  });

  // Read course_id / product_id and coupon from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const idFromUrl =
      params.get("course_id") ||
      params.get("product_id") ||
      params.get("id") ||
      "";
    const couponFromUrl = params.get("coupon") || "";

    if (idFromUrl) setCourseId(idFromUrl.trim());
    if (couponFromUrl) setCouponCode(couponFromUrl.trim());
  }, []);

  // Thank-you modal sync via hash
  useEffect(() => {
    const syncThankYou = () => {
      setShowThankYou(window.location.hash === thankYouPath);
    };
    syncThankYou();
    window.addEventListener("hashchange", syncThankYou);
    return () => window.removeEventListener("hashchange", syncThankYou);
  }, []);

  // Fetch course whenever courseId changes
  useEffect(() => {
    if (!courseId) {
      setFetchingCourse(false);
      return;
    }
    fetchCourse(courseId);
  }, [courseId]);

  async function fetchCourse(resolvedCourseId) {
    try {
      setFetchingCourse(true);
      const res = await Apirequests(
        `courses/get-course/${resolvedCourseId}`,
        "GET",
        {},
        {},
      );

      if (res?.success) {
        setCourseData(res.data);
        const price = Number(res?.data?.course_price || 0);
        const discount = Number(res?.data?.course_discount_percentage || 0);
        const priceAfterDiscount = Math.ceil(price - (price * discount) / 100);
        setFinalPrice(priceAfterDiscount);
        setDiscountPercentage(discount);
      } else {
        toast.error(res?.message || "Course not found");
      }
    } catch (error) {
      console.log("Course fetch failed", error);
      toast.error("Failed to fetch course details");
    } finally {
      setFetchingCourse(false);
    }
  }

  // Auto-apply coupon when courseData is ready
  useEffect(() => {
    const applyCoupon = async () => {
      if (!couponCode || !courseData?._id) return;
      try {
        const res = await Apirequests(
          "coupons/apply-coupon",
          "POST",
          { coupon_code: couponCode, course_id: courseData._id },
          {},
        );
        if (res?.success && res?.data?.final_price != null) {
          const newFinalPrice = Math.ceil(Number(res.data.final_price));
          setFinalPrice(newFinalPrice);
          const baseCoursePrice = Number(courseData?.course_price || 0);
          if (baseCoursePrice > 0) {
            const couponDiscount =
              ((baseCoursePrice - Number(res.data.final_price)) /
                baseCoursePrice) *
              100;
            setDiscountPercentage(Math.ceil(couponDiscount));
          }
          toast.success(`Coupon ${couponCode} applied`);
        }
      } catch (error) {
        console.log("Coupon apply failed", error);
      }
    };
    applyCoupon();
  }, [couponCode, courseData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, whatsapp_number: value || "" }));
  };

  const emailValid = useMemo(() => {
    const value = String(formData.email || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }, [formData.email]);

  const currency = courseData?.notes?.currency || "INR";
  const currencySymbol = currency === "INR" ? "₹" : "$";

  function formatPrice(value) {
    return `${currencySymbol}${Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: currency === "INR" ? 0 : 2,
      maximumFractionDigits: currency === "INR" ? 0 : 2,
    })}`;
  }

  const displayOriginalPrice = Number(courseData?.course_price || 0);
  const saveAmount = Math.max(displayOriginalPrice - finalPrice, 0);

  async function startRazorpayPayment() {
    if (loading) return;

    if (!courseData) {
      toast.error("Course details not loaded yet");
      return;
    }

    const finalData = {
      name: String(formData.name || "").trim(),
      email: String(formData.email || "").trim(),
      phone: String(formData.whatsapp_number || "").trim(),
    };

    if (!finalData.name) return toast.error("Name is required");
    if (!finalData.email) return toast.error("Email is required");
    if (!finalData.phone) return toast.error("Phone number is required");
    if (!emailValid) return toast.error("Please enter a valid email");

    try {
      setLoading(true);


      const notes = {
        country_code:  "+91",
        country: "INDIA",
        timezone: "Asia/Calcutta",
      };

      const paymentRes = await handlePaymentNew(
        courseData?.course_id || courseId,
        finalData,
        "course",
        {},
        couponCode || "",
        {},
        notes,
        currency,
      );

      if (!paymentRes?.order_id) {
        toast.error(paymentRes?.message || "Unable to create payment order");
        return;
      };


      console.log(paymentRes,'payres')

      await handleRazorPayPayment(
        finalData,
        paymentRes.order_id,
        setFormData,
        thankYouPath,
        Razorpay,
        null,
        paymentRes?.data?.invoice_id
      );
    } catch (error) {
      console.log(error);
      toast.error("Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function closeThankYou() {
    setShowThankYou(false);
    if (window.location.hash === thankYouPath) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
  }

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#1a0a3a] via-[#0d0a1a] to-[#0d0a1a] px-4 py-16 font-sans">
        <div className="w-full max-w-md bg-[#100d22] border border-white/10 rounded-2xl p-7 shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-10 bg-[#d4a044]/70" />
            <span className="text-[11px] font-semibold tracking-[0.2em] text-[#d4a044] uppercase">
              Test Payment
            </span>
            <div className="h-px w-10 bg-[#d4a044]/70" />
          </div>

          <h2 className="text-center text-white text-2xl font-bold mb-1">
            Razorpay Checkout
          </h2>
          <p className="text-center text-[#a89ec0] text-xs mb-6">
            Dynamic test payment via URL params
          </p>

          {/* Loading course */}
          {fetchingCourse ? (
            <div className="flex flex-col items-center justify-center py-10 text-[#a89ec0]">
              <Loader2 className="animate-spin mb-3" size={28} />
              <p className="text-sm">Loading course details...</p>
            </div>
          ) : !courseData ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
              <p className="text-red-300 text-sm font-semibold mb-1">
                No course found
              </p>
              <p className="text-red-200/70 text-xs">
                Pass{" "}
                <code className="bg-black/30 px-1.5 py-0.5 rounded">
                  ?course_id=YOUR_ID
                </code>{" "}
                in the URL
              </p>
            </div>
          ) : (
            <>
              {/* Course info */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-5">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#7a6fa0] font-semibold mb-1">
                  Course
                </p>
                <p className="text-white font-semibold text-sm leading-snug">
                  {courseData?.course_title || courseData?.course_name || "Untitled"}
                </p>
                <p className="text-[#7a6fa0] text-[11px] mt-1 break-all">
                  ID: {courseData?.course_id || courseId}
                </p>
              </div>

              {/* Price */}
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#7a6fa0] font-semibold mb-2">
                Course Price
              </p>
              <div className="flex items-baseline flex-wrap mb-2">
                <span className="text-white text-4xl font-bold leading-none">
                  {formatPrice(finalPrice)}
                </span>
                {saveAmount > 0 && (
                  <span className="text-[#6a5f85] text-base line-through ml-2">
                    {formatPrice(displayOriginalPrice)}
                  </span>
                )}
              </div>

              {saveAmount > 0 && (
                <div className="inline-block bg-[#2a1f00] text-[#d4a044] text-[11px] font-semibold rounded-full px-3.5 py-1 mb-4 border border-[#d4a044]/30">
                  You Save {formatPrice(saveAmount)}
                  {discountPercentage ? ` (${discountPercentage}% OFF)` : ""}
                </div>
              )}

              {couponCode && (
                <div className="text-[11px] text-emerald-300 mb-4">
                  Coupon applied: <span className="font-semibold">{couponCode}</span>
                </div>
              )}

              {/* Name */}
              <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-lg px-3.5 py-3 mb-2.5 focus-within:border-[#9b6fff]/50 transition-colors">
                <User size={15} className="text-[#5e5480] shrink-0" />
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-transparent outline-none border-none text-[#d0cce8] text-[13px] w-full placeholder:text-[#5e5480]"
                />
              </div>

              {/* Email */}
              <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-lg px-3.5 py-3 mb-2.5 focus-within:border-[#9b6fff]/50 transition-colors">
                <Mail size={15} className="text-[#5e5480] shrink-0" />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-transparent outline-none border-none text-[#d0cce8] text-[13px] w-full placeholder:text-[#5e5480]"
                />
              </div>

              {/* Phone */}
              <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-lg px-3.5 py-3 mb-2.5 focus-within:border-[#9b6fff]/50 transition-colors [&_.PhoneInput]:flex [&_.PhoneInput]:items-center [&_.PhoneInput]:w-full [&_.PhoneInput]:gap-2.5 [&_.PhoneInputCountrySelect]:bg-transparent [&_.PhoneInputCountrySelect]:border-none [&_.PhoneInputCountrySelect]:text-[#d0cce8] [&_.PhoneInputCountrySelect]:outline-none [&_.PhoneInputCountrySelect]:text-[13px] [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:border-none [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:text-[#d0cce8] [&_.PhoneInputInput]:text-[13px] [&_.PhoneInputInput]:w-full [&_.PhoneInputInput]:min-w-0 [&_.PhoneInputInput]:placeholder:text-[#5e5480]">
                <Phone size={15} className="text-[#5e5480] shrink-0" />
                 <input
                  type="tel"
                  name="whatsapp_number"
                  placeholder="Phone"
                  value={formData.whatsapp_number}
                  onChange={handleChange}
                  className="bg-transparent outline-none border-none text-[#d0cce8] text-[13px] w-full placeholder:text-[#5e5480]"
                />
              </div>

              {/* CTA */}
              <button
                onClick={startRazorpayPayment}
                disabled={loading}
                className="w-full mt-4 py-3.5 px-4 rounded-xl bg-gradient-to-br from-[#6d35e8] to-[#9b6fff] text-white font-semibold text-[15px] flex items-center justify-center gap-2 shadow-lg shadow-[#6d35e8]/40 hover:shadow-[#6d35e8]/60 hover:-translate-y-px transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {loading ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Opening payment...
                  </>
                ) : (
                  <>
                    <Lock size={15} />
                    Pay {formatPrice(finalPrice)} with Razorpay
                  </>
                )}
              </button>

              {/* Guarantee */}
              <div className="flex items-start gap-2.5 bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-3.5 py-3 mt-4">
                <ShieldCheck size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-[12px] text-emerald-200/80 leading-relaxed m-0">
                  <span className="text-emerald-400 font-semibold">
                    Secure Razorpay Checkout
                  </span>{" "}
                  — This is a test payment page. Use Razorpay test credentials.
                </p>
              </div>

              {/* Test info footer */}
              <div className="mt-5 pt-4 border-t border-white/5 flex items-center gap-2 text-[10px] text-[#7a6fa0]">
                <CreditCard size={12} />
                <span>
                  Card: 4111 1111 1111 1111 · Any CVV · Any future date
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Thank you modal */}
      {showThankYou && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-5"
          onClick={closeThankYou}
        >
          <div
            className="w-full max-w-sm bg-[#100d22] border border-white/10 rounded-2xl p-7 shadow-2xl text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white text-2xl font-bold mb-2.5">
              Payment Successful
            </h3>
            <p className="text-[#a89ec0] text-sm leading-relaxed mb-4">
              Test payment completed successfully.
            </p>
            <button
              type="button"
              onClick={closeThankYou}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-br from-[#6d35e8] to-[#9b6fff] text-white font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}